import fire, { fuego } from '@store/firebase';
import { CollectionPath, SubColPath } from '@typings/collections.enum';
import { cookieStore } from '@store/cookie/cookieStore';
import { setAnonymousProfile, setUserProfile } from './updateUser';
import React from 'react';
import ReactDOM from 'react-dom';
import Toast from '@components/dataDisplay/Toast';

const auth = fire.auth();
const firestore = fire.firestore();

// 🔧 Firebase Auth 언어를 한국어로 설정
auth.languageCode = 'ko';
// 또는 사용자의 브라우저 언어 사용: auth.useDeviceLanguage();

// 익명 유저 가입
export async function registerAnonymousUser() {
  auth
    .signInAnonymously()
    .then(async () => {
      // 유저가 익명 로그인 된 후
      auth.onAuthStateChanged((user) => {
        if (user) {
          cookieStore.setUserToken(user && user.refreshToken);
          const uid = user.uid;
          setAnonymousProfile(uid);
        } else {
          // User is signed out
        }
      });
    })
    .catch((err) => console.error('set anonymous user', err.type, err.message));
}

// 익명 유저 로그인
export async function loginAnonymous() {
  auth.onAuthStateChanged((user) => {
    if (user) {
      cookieStore.setUserToken(user && user.refreshToken);
    } else {
      // User is signed out
    }
  });
}

// 회원가입
export async function registerWithEmail({
  email,
  password,
  displayName,
  requestMessage,
}) {
  const userCredential = await auth.createUserWithEmailAndPassword(
    email,
    password,
  );
  const currentAuth = userCredential.user;

  const currentUser = await loginWithEmail({ email, password });
  // await sendVerificationMail(); // 인증이메일 전송

  if (currentUser) {
    await setUserProfile({ user: currentUser, displayName, requestMessage });
  }
  return currentAuth;
}

async function sendVerificationMail() {
  if (auth.currentUser) {
    await auth.currentUser.sendEmailVerification();
  }
}

// Toast 메시지 표시 함수
function showToast(message: string) {
  // 기존 toast 제거
  const existingToast = document.getElementById('toast-root');
  if (existingToast) {
    existingToast.remove();
  }

  // 새로운 toast 컨테이너 생성
  const toastContainer = document.createElement('div');
  toastContainer.style.position = 'fixed';
  toastContainer.style.top = '0';
  toastContainer.style.left = '0';
  toastContainer.style.width = '100%';
  toastContainer.style.height = '100%';
  toastContainer.style.pointerEvents = 'none';
  toastContainer.style.zIndex = '9999';
  document.body.appendChild(toastContainer);

  // ReactDOM.render 사용
  ReactDOM.render(<Toast contents={message} />, toastContainer);

  // 3초 후 자동으로 제거
  setTimeout(() => {
    ReactDOM.unmountComponentAtNode(toastContainer);
    toastContainer.remove();
  }, 3500);
}

// 비밀번호 재설정 (Toast 사용)
export async function sendPasswordResetEmail(userEmail: string) {
  try {
    console.log('Password reset attempt for:', userEmail);

    // Firebase에서 직접 처리하도록 변경 (사용자 존재 여부 확인 제거)
    await auth.sendPasswordResetEmail(userEmail);
    console.log('Password reset email sent successfully');

    const successMessage = '비밀번호 재설정 이메일이 전송되었습니다.';
    showToast(successMessage);

    return {
      success: true,
      message: successMessage,
    };
  } catch (error: any) {
    console.error('Password reset email error:', error);

    // Firebase Auth 에러 코드별 메시지
    let errorMessage = '비밀번호 재설정 이메일 전송에 실패했습니다.';

    switch (error.code) {
      case 'auth/user-not-found':
        errorMessage = '해당 이메일로 등록된 사용자가 없습니다.';
        break;
      case 'auth/invalid-email':
        errorMessage = '유효하지 않은 이메일 형식입니다.';
        break;
      case 'auth/user-disabled':
        errorMessage = '비활성화된 사용자 계정입니다.';
        break;
      case 'auth/too-many-requests':
        errorMessage = '너무 많은 요청이 발생했습니다. 잠시 후 다시 시도해주세요.';
        break;
      case 'auth/network-request-failed':
        errorMessage = '네트워크 연결을 확인해주세요.';
        break;
      default:
        errorMessage = error.message || errorMessage;
    }

    // 토스트로 에러 메시지 표시
    showToast(errorMessage);

    return {
      success: false,
      message: errorMessage,
      error: error.code,
    };
  }
}

// 간단한 alert 사용 버전 (백업)
export async function sendPasswordResetEmailWithAlert(userEmail: string) {
  try {
    console.log('🔄 Password reset attempt for:', userEmail);
    console.log('🔧 Firebase Auth 설정 확인:', {
      authDomain: (auth.app.options as any).authDomain || 'unknown',
      projectId: (auth.app.options as any).projectId || 'unknown',
      languageCode: auth.languageCode,
      currentUser: auth.currentUser?.email || 'none'
    });

    // Firebase에서 직접 처리하도록 변경 (사용자 존재 여부 확인 제거)
    // 🔧 이메일 전송 설정 개선
    const actionCodeSettings = {
      // 사용자가 이메일 링크를 클릭한 후 리디렉션될 URL
      url: window.location.origin + '/login',
      // iOS 앱에서 링크를 처리할지 여부
      handleCodeInApp: false,
    };

    await auth.sendPasswordResetEmail(userEmail, actionCodeSettings);

    const successMessage = `비밀번호 재설정 이메일이 ${userEmail}로 전송되었습니다.\n\n스팸메일함도 확인해주세요.`;

    return {
      success: true,
      message: successMessage,
    };
  } catch (error: any) {
    console.error('❌ Password reset email error:', error);
    console.error('❌ Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack,
      email: userEmail
    });

    // Firebase Auth 에러 코드별 메시지
    let errorMessage = '비밀번호 재설정 이메일 전송에 실패했습니다.';

    switch (error.code) {
      case 'auth/user-not-found':
        errorMessage = '해당 이메일로 등록된 사용자가 없습니다.\n\n다른 이메일로 시도해보세요.';
        break;
      case 'auth/invalid-email':
        errorMessage = '유효하지 않은 이메일 형식입니다.\n\n올바른 이메일 주소를 입력해주세요.';
        break;
      case 'auth/user-disabled':
        errorMessage = '비활성화된 사용자 계정입니다.\n\n관리자에게 문의해주세요.';
        break;
      case 'auth/too-many-requests':
        errorMessage = '너무 많은 요청이 발생했습니다.\n\n잠시 후 다시 시도해주세요.';
        break;
      case 'auth/network-request-failed':
        errorMessage = '네트워크 연결을 확인해주세요.';
        break;
      default:
        errorMessage = error.message || errorMessage;
    }

    // alert로 에러 메시지 표시
    alert(`❌ 비밀번호 재설정 실패\n\n${errorMessage}`);

    return {
      success: false,
      message: errorMessage,
      error: error.code,
    };
  }
}


// 로그인
export async function loginWithEmail({ email, password }) {
  const { user } = await auth.signInWithEmailAndPassword(email, password);
  cookieStore.setUserToken(user && user.refreshToken);
  return user;
}

// 유저 권한 업데이트
export async function updateUserAuthorizationType(
  uid: string,
  authType: string,
) {
  const userRef = firestore.collection(CollectionPath.USERS).doc(uid);

  await userRef.set({ authorizationType: authType }, { merge: true });
}

// 로그아웃
export async function userLogout(shouldRedirect: boolean = true) {
  // 1. 쿠키 전체 초기화 (all=true: 익명 관련 쿠키까지 포함)
  cookieStore.clearTokens(true);

  // 2. localStorage에 남은 SWR / Firebase 캐시 및 게이트 통과 캐시 제거
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      // SWR 캐시, Firebase persistence 관련 키만 선별 삭제
      if (
        key.startsWith('swr-') ||
        key.startsWith('firebase:') ||
        key.startsWith('fuego-') ||
        key.startsWith('firestore/')
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));

    // 게이트 통과 캐시 초기화: 로그아웃/탈퇴 후 다시 방문하면 게이트가 표시되어야 함
    localStorage.removeItem('moogong_gate_page_passed');
  } catch (_) {
    // localStorage 접근 불가 환경(private 모드 등)은 무시
  }

  // 3. Firebase Auth 로그아웃 — signOut()이 완료된 뒤 리다이렉트
  await fire.auth().signOut();

  // 4. 홈으로 리다이렉트
  if (shouldRedirect) {
    window.location.href = '/';
  }
}

// 기존계정 연동계정으로 추가
export async function loginAndAddAccount({
  prevUid,
  prevEmail,
  prevPassword,
  email,
  password,
}: {
  prevUid: string;
  prevEmail: string;
  prevPassword: string;
  email: string;
  password: string;
}) {
  await loginWithEmail({ email, password });
  const accountUid = fuego.auth().currentUser?.uid;

  // 1. 기존 계정(A)에 신규 계정(B)을 추가
  const prevRef = fuego.db.collection(
    `${CollectionPath.USERS}/${prevUid}/${SubColPath.ACCOUNTS}`,
  );
  await prevRef
    .doc(accountUid)
    .set({ uid: accountUid, email, password, createdAt: new Date() });

  // 2. 신규 계정(B)에도 기존 계정(A)을 추가 (email/password 포함하여 역방향 전환 가능)
  const ref = fuego.db.collection(
    `${CollectionPath.USERS}/${accountUid}/${SubColPath.ACCOUNTS}`,
  );
  return ref.doc(prevUid).set(
    { uid: prevUid, email: prevEmail, password: prevPassword, createdAt: new Date() },
    { merge: true },
  );
}

// 연동계정에서 삭제
export async function removeAccount({ currentUid, targetUid }) {
  const ref = fuego.db.collection(
    `${CollectionPath.USERS}/${currentUid}/${SubColPath.ACCOUNTS}`,
  );
  return ref.doc(targetUid).delete();
}

// 계정 탈퇴
export async function deleteUserDoc({ uid }) {
  const userRef = firestore.collection(CollectionPath.USERS).doc(uid);
  return userRef.delete();
}

export async function deleteUser() {
  if (auth.currentUser) {
    await auth.currentUser.delete();
    // 로그인 인증을 한 번 더 받아야 함
    // removeUserToken();
  }
}
